import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useField } from '@unform/core';
import {
  ActionButton,
  Container,
  FileActions,
  FileErrorList,
  FileName,
  FilePreviewContainer,
  FileTypes,
  ImagePreview,
  InputError,
  ModerationOverlay,
  ModerationStatusBadge,
  PreviewContainer,
  PreviewItem,
  TrashFileButton,
  UploadArea,
  UploadText,
  VideoPreview,
} from './styles';

import {
  AddIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
} from '../../assets/svg/SVGComponents';
import type { ModerationEntry } from '../../hooks/useImageModeration';
import {
  ALLOWED_VIDEO_MIME_TYPES,
  formatMegabytes,
  formatMinutes,
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_DURATION_SECONDS,
  MAX_VIDEO_SIZE_BYTES,
  probeVideo,
  resolveMimeType,
} from '../../utils/mediaConstraints';

interface InputFileProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  /** Called when a new file is added (with its local id and File object) */
  onFileAdded?: (fileId: string, file: File) => void;
  /** Called when a file is removed */
  onFileRemoved?: (fileId: string) => void;
  /** Per-file moderation entries keyed by file id */
  moderationEntries?: Record<string, ModerationEntry>;
}

export interface IFileUploadProps {
  id: string;
  file: File;
  preview: string;
  type: string;
}

const FileUploadInput: React.FC<InputFileProps> = ({
  name,
  onFileAdded,
  onFileRemoved,
  moderationEntries,
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [files, setFiles] = useState<IFileUploadProps[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const updateFiles = useCallback(
    (newFiles: React.SetStateAction<IFileUploadProps[]>) => {
      setFiles(currentFiles => {
        const filesArray =
          typeof newFiles === 'function' ? newFiles(currentFiles) : newFiles;
        // Forçar atualização do valor no Unform
        if (inputRef.current) {
          inputRef.current.files = filesArray.reduce((dataTransfer, file) => {
            dataTransfer.items.add(file.file);
            return dataTransfer;
          }, new DataTransfer()).files;
        }
        return filesArray;
      });
    },
    [],
  );

  /**
   * Devolve a mensagem de erro do arquivo, ou null quando ele pode ser
   * anexado. A duração do vídeo só é lida depois que formato e tamanho
   * passaram — não vale carregar metadados de um arquivo que já foi recusado.
   */
  const validateFile = useCallback(
    async (file: File): Promise<string | null> => {
      // Nunca `file.type` cru: o iPhone entrega .MOV com tipo vazio e o
      // arquivo era recusado aqui, antes mesmo de tentar o upload.
      const mimeType = resolveMimeType(file);
      const fileType = mimeType.split('/')[0];

      if (fileType !== 'image' && fileType !== 'video') {
        return `Tipo de arquivo não suportado: ${file.name}`;
      }

      if (fileType === 'image') {
        return file.size > MAX_IMAGE_SIZE_BYTES
          ? `Imagem "${file.name}" passa de ${formatMegabytes(MAX_IMAGE_SIZE_BYTES)}`
          : null;
      }

      if (!ALLOWED_VIDEO_MIME_TYPES.includes(mimeType)) {
        return `Vídeo "${file.name}" está num formato que não conseguimos analisar. Envie em MP4 ou MOV.`;
      }

      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        return `Vídeo "${file.name}" passa de ${formatMegabytes(MAX_VIDEO_SIZE_BYTES)}`;
      }

      const { durationSeconds, isDecodable } = await probeVideo(file);

      // Antes disto, um vídeo HEVC atravessava toda a validação, subia para o
      // S3 e só virava problema no feed, como um quadrado preto que ninguém
      // conseguia assistir. Melhor recusar aqui, explicando o porquê.
      if (!isDecodable) {
        return `Não conseguimos abrir o vídeo "${file.name}". Celulares gravam em HEVC por padrão, um formato que a maioria dos navegadores não reproduz. No iPhone: Ajustes › Câmera › Formatos › “Mais compatível”. Ou envie o vídeo em MP4.`;
      }

      if (
        durationSeconds !== null &&
        durationSeconds > MAX_VIDEO_DURATION_SECONDS
      ) {
        return `Vídeo "${file.name}" passa de ${formatMinutes(MAX_VIDEO_DURATION_SECONDS)}. Corte um trecho menor e envie de novo.`;
      }

      return null;
    },
    [],
  );

  const createPreviews = useCallback(
    async (newFiles: FileList) => {
      // Array.from roda antes do primeiro await, então a FileList é copiada
      // enquanto ainda é válida — o input pode ser limpo logo depois.
      const candidates = Array.from(newFiles);
      const validations = await Promise.all(candidates.map(validateFile));

      const validFiles = candidates.filter(
        (_, index) => validations[index] === null,
      );
      const newErrors = validations.filter(
        (message): message is string => message !== null,
      );

      setErrors(prev => [...prev, ...newErrors]);

      const previewFiles = validFiles.map(newFile => ({
        id: Math.random().toString(36).substring(2, 9),
        file: newFile,
        preview: URL.createObjectURL(newFile),
        type: resolveMimeType(newFile).split('/')[0] as
          | 'image'
          | 'video'
          | 'gif',
      }));
      // Notify parent about newly added files (for moderation)
      previewFiles.forEach(pf => {
        onFileAdded?.(pf.id, pf.file);
      });
      updateFiles((prev: IFileUploadProps[]) => [...prev, ...previewFiles]);
    },
    [updateFiles, onFileAdded, validateFile],
  );

  const handleDrag = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === 'dragenter' || event.type === 'dragover');
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      setErrors([]); // Limpa erros anteriores
      if (event.dataTransfer.files) {
        void createPreviews(event.dataTransfer.files);
      }
    },
    [createPreviews],
  );

  const handleInputchange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        setErrors([]); // Limpa erros anteriores
        void createPreviews(event.target.files);
        event.target.value = '';
      }
    },
    [createPreviews],
  );

  const triggerFileInput = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const removeFile = useCallback(
    (id: string) => {
      const removedFile = files.find(file => file.id === id);
      updateFiles(prev => prev.filter(file => file.id !== id));
      onFileRemoved?.(id);

      // Remove erros relacionados ao arquivo removido
      if (removedFile) {
        setErrors(prev =>
          prev.filter(err => !err.includes(removedFile.file.name)),
        );
      }
    },
    [files, updateFiles, onFileRemoved],
  );

  const moveFile = useCallback(
    (id: string, direction: 'left' | 'right') => {
      updateFiles(prev => {
        const index = prev.findIndex(file => file.id === id);
        if (index === -1) return prev;

        const newIndex = direction === 'left' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= prev.length) return prev;

        const newFiles = [...prev];
        [newFiles[index], newFiles[newIndex]] = [
          newFiles[newIndex],
          newFiles[index],
        ];

        return newFiles;
      });
    },
    [updateFiles],
  );

  useEffect(() => {
    return () => {
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [files]);

  // Add helper function to render file preview
  const renderPreview = (file: IFileUploadProps): JSX.Element => {
    if (file.type === 'image') {
      return <ImagePreview src={file.preview} alt="Preview" />;
    }
    if (file.type === 'video') {
      return (
        // `src` direto, sem <source type>: com o type declarado o navegador
        // confia nele e nem tenta decodificar — o Chrome responde "não sei
        // tocar" para video/quicktime, e o .MOV do iPhone chega com type
        // vazio. Nos dois casos sobrava o fundo preto do elemento.
        // O #t=0.1 força um seek para pintar o primeiro quadro; sem ele o
        // vídeo fica preto até alguém apertar play.
        <VideoPreview
          src={`${file.preview}#t=0.1`}
          controls
          preload="metadata"
        />
      );
    }
    return (
      <FilePreviewContainer>
        <AddIcon width="2rem" />
        <FileName>{file.file.name}</FileName>
      </FilePreviewContainer>
    );
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => files.map(file => file.file),
      clearValue: () => {
        setFiles([]);
        if (inputRef.current) inputRef.current.value = '';
      },
      setValue: (_, value: File[]) => {
        if (value) {
          const newFiles = value.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            preview: URL.createObjectURL(file),
            type: file.type.split('/')[0] as 'image' | 'video' | 'other',
          }));
          setFiles(newFiles);
        }
      },
    });
  }, [fieldName, files, registerField]);

  // Moderation status label & badge color helper
  const getModerationLabel = (
    entry: ModerationEntry | undefined,
  ): {
    label: string;
    variant:
      | 'loading'
      | 'approved'
      | 'rejected'
      | 'error'
      | 'unsupported'
      | 'review';
  } | null => {
    switch (entry?.status) {
      case 'UPLOADING':
        return { label: 'Analisando…', variant: 'loading' };
      case 'APPROVED':
        // Vídeo entra no ar já, mas com avaliação humana pendente.
        return entry.requiresReview
          ? { label: 'Em avaliação', variant: 'review' }
          : { label: 'Aprovado', variant: 'approved' };
      case 'REJECTED':
        return { label: 'Rejeitado', variant: 'rejected' };
      case 'UNSUPPORTED':
        return { label: 'Não enviado', variant: 'unsupported' };
      case 'ERROR':
        return { label: 'Erro', variant: 'error' };
      default:
        return null;
    }
  };

  return (
    <Container>
      {error && <InputError>{error}</InputError>}
      <UploadArea
        onDragEnter={event => {
          handleDrag(event);
        }}
        onDragLeave={event => {
          handleDrag(event);
        }}
        onDragOver={event => {
          handleDrag(event);
        }}
        onDrop={event => {
          handleDrop(event);
        }}
        $dragActive={dragActive}
        onClick={triggerFileInput}
      >
        <input
          name={name}
          ref={inputRef}
          type="file"
          multiple
          onChange={event => {
            handleInputchange(event);
          }}
          accept="image/*, video/*"
          style={{ display: 'none' }}
        />

        <UploadText>
          Arraste arquivos aqui ou clique para selecionar
          <FileTypes>
            Fotos até 10MB (JPG, PNG, WEBP ou GIF) e vídeos até 100MB e 3
            minutos (MP4 ou MOV)
          </FileTypes>
        </UploadText>
      </UploadArea>

      {errors.length > 0 && (
        <FileErrorList>
          {errors.map((message, index) => (
            <li key={`${index}-${message}`}>{message}</li>
          ))}
        </FileErrorList>
      )}

      <PreviewContainer>
        {files.map((file, index) => {
          const entry = moderationEntries?.[file.id];
          const badge = getModerationLabel(entry);
          const isUnsupported = entry?.status === 'UNSUPPORTED';
          const showOverlay =
            isUnsupported ||
            entry?.status === 'REJECTED' ||
            entry?.status === 'ERROR';

          return (
            <PreviewItem key={file.id}>
              <TrashFileButton onClick={() => removeFile(file.id)}>
                <TrashIcon
                  width="1.1rem"
                  stroke="#1E88E5"
                  strokeWidth={1}
                  fill="none"
                />
              </TrashFileButton>
              {renderPreview(file)}

              {/* Moderation overlay for rejected / unsupported / error files */}
              {showOverlay && (
                <ModerationOverlay
                  title={entry?.message}
                  $variant={isUnsupported ? 'unsupported' : 'rejected'}
                >
                  {entry?.message || 'Arquivo rejeitado'}
                </ModerationOverlay>
              )}

              {/* Moderation status badge */}
              {badge && (
                <ModerationStatusBadge
                  $variant={badge.variant}
                  title={entry?.message}
                >
                  {badge.label}
                </ModerationStatusBadge>
              )}

              <FileActions>
                <ActionButton
                  onClick={() => moveFile(file.id, 'left')}
                  disabled={index === 0}
                >
                  <ChevronLeftIcon
                    width="1.6rem"
                    stroke="#1e88e5"
                    strokeWidth={3}
                  />
                </ActionButton>

                <ActionButton
                  onClick={() => moveFile(file.id, 'right')}
                  disabled={index === files.length - 1}
                >
                  <ChevronRightIcon
                    width="1.6rem"
                    stroke="#1e88e5"
                    strokeWidth={3}
                  />
                </ActionButton>
              </FileActions>
            </PreviewItem>
          );
        })}
      </PreviewContainer>
    </Container>
  );
};

export default FileUploadInput;
